// ============================================================================
// FSA DEPRECIATION — ASSET ADDITION MODAL  (ADR-0006)
// ============================================================================
// Add/Edit one asset addition in the Depreciation tab's in-memory draft.
// Persistence happens at tab level (Save on the Depreciation tab), so this
// modal only validates and hands the entry back via onSave. Days / <180 flag
// / book dep / indicative IT dep compute live as the user types.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import type {
  DepCalcContext,
  DepClassConfig,
} from 'api/types/fsa/depreciationCalc.types';
import {
  newLocalId,
  previewAddition,
  roundTo,
  type DraftAddition,
} from 'components/modules/fsa/engagement/depreciation/depCalcEngine';

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** Entry being edited (null for create). */
  entry: DraftAddition | null;
  context: DepCalcContext;
  classConfig: Record<string, DepClassConfig>;
  roundingPrecision: number;
  bookProrataConvention: 'DAY_WISE' | 'HALF_RATE_180';
  onSave: (entry: DraftAddition) => void;
  onClose: () => void;
}

interface FormData {
  classCode: string;
  ledgerName: string;
  description: string;
  additionDate: string;
  putToUseDate: string;
  putToUseTouched: boolean;
  amount: string;
  itBlockEnumId: string;
  itRateOverride: string;
}

const INITIAL_FORM: FormData = {
  classCode: '',
  ledgerName: '',
  description: '',
  additionDate: '',
  putToUseDate: '',
  putToUseTouched: false,
  amount: '',
  itBlockEnumId: '',
  itRateOverride: '',
};

const FsaDepAdditionModal: React.FC<Props> = ({
  isOpen, mode, entry, context, classConfig, roundingPrecision,
  bookProrataConvention, onSave, onClose,
}) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const initialSnapshotRef = useRef('');

  useEffect(() => {
    if (!isOpen) return;
    const data: FormData = entry
      ? {
          // Picker value = sub-class key when the entry sits on one.
          classCode: entry.subClassLocalId != null ? String(entry.subClassLocalId) : entry.classCode,
          ledgerName: entry.ledgerName ?? '',
          description: entry.description,
          additionDate: entry.additionDate,
          putToUseDate: entry.putToUseDate,
          putToUseTouched: entry.putToUseDate !== entry.additionDate,
          amount: String(entry.amount || ''),
          itBlockEnumId: entry.itBlockEnumId != null ? String(entry.itBlockEnumId) : '',
          itRateOverride: entry.itRateOverride != null ? String(entry.itRateOverride) : '',
        }
      : { ...INITIAL_FORM };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  /** Maps a picker value (class code OR sub-class local id) back to (parent class, sub key). */
  const resolveSelection = useCallback((value: string): { classCode: string; subClassLocalId: string | null } => {
    const cfg = classConfig[value];
    if (cfg?.parent_class_code) return { classCode: cfg.parent_class_code, subClassLocalId: value };
    return { classCode: value, subClassLocalId: null };
  }, [classConfig]);

  const setField = useCallback(<K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => {
      const next = { ...prev, [key]: value };
      // Put-to-use defaults to the addition date until explicitly changed.
      if (key === 'additionDate' && !prev.putToUseTouched) {
        next.putToUseDate = value as string;
      }
      if (key === 'putToUseDate') next.putToUseTouched = true;
      // Classification change re-suggests the IT block from the class default
      // (a sub-class inherits its parent class's default).
      if (key === 'classCode') {
        const parentCode = resolveSelection(value as string).classCode;
        const opt = context.classOptions.find(o => o.classCode === parentCode);
        const suggested = opt?.defaultItBlock
          ? context.itBlockOptions.find(b => b.enumKey === opt.defaultItBlock)
          : undefined;
        next.itBlockEnumId = suggested ? String(suggested.id) : prev.itBlockEnumId;
      }
      return next;
    });
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [context.classOptions, context.itBlockOptions, resolveSelection]);

  // Classes + depreciation-internal sub-classes (rev 6). The picker value is
  // either a class code or a sub-class local id; resolveSelection maps it
  // back to (parent class, sub key).
  const classOptions = useMemo(() => {
    const opts = context.classOptions.map(o => ({
      value: o.classCode,
      label: `${o.className} (${o.scheduleCode === 'NOTE_INTANGIBLE_ASSETS' ? 'Intangible' : 'PPE'})`,
    }));
    for (const [key, cfg] of Object.entries(classConfig)) {
      if (!cfg.parent_class_code) continue;
      const parent = context.classOptions.find(o => o.classCode === cfg.parent_class_code);
      opts.push({ value: key, label: `${parent?.className ?? cfg.parent_class_code} — ${cfg.name ?? key}` });
    }
    return opts;
  }, [context.classOptions, classConfig]);

  const blockOptions = useMemo(() => context.itBlockOptions.map(o => ({
    value: String(o.id),
    label: o.enumValue,
  })), [context.itBlockOptions]);

  // TB fixed-asset ledgers + the current free-text value (creatable keeps
  // enterprise SyutSelect styling while still allowing any reference).
  const ledgerSelectOptions = useMemo(() => {
    const opts = context.ledgerOptions.map(l => ({ value: l.ledgerName, label: l.ledgerName }));
    if (formData.ledgerName && !opts.some(o => o.value === formData.ledgerName)) {
      opts.unshift({ value: formData.ledgerName, label: formData.ledgerName });
    }
    return opts;
  }, [context.ledgerOptions, formData.ledgerName]);

  const selectedClassOption = context.classOptions.find(
    o => o.classCode === resolveSelection(formData.classCode).classCode);
  const suggestedBlockKey = selectedClassOption?.defaultItBlock ?? null;
  const selectedBlock = context.itBlockOptions.find(b => String(b.id) === formData.itBlockEnumId);
  const blockIsSuggested = suggestedBlockKey != null && selectedBlock?.enumKey === suggestedBlockKey;

  // Live preview — same math as the engine.
  const preview = useMemo(() => {
    const amount = parseFloat(formData.amount) || 0;
    // Sub-class rate first (picker value is the sub key), parent fallback.
    const classRate = classConfig[formData.classCode]?.book_rate
      ?? classConfig[resolveSelection(formData.classCode).classCode]?.book_rate
      ?? 0;
    const itRate = formData.itRateOverride !== ''
      ? parseFloat(formData.itRateOverride) || 0
      : selectedBlock?.rate ?? 0;
    if (!formData.putToUseDate || amount === 0) return null;
    return previewAddition(
      { putToUseDate: formData.putToUseDate, amount, classRate, itRate, convention: bookProrataConvention },
      context,
    );
  }, [formData, classConfig, selectedBlock, bookProrataConvention, context, resolveSelection]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.description.trim()) errors.description = 'Description is required.';
    if (!formData.classCode) errors.classCode = 'Asset classification is required.';
    if (!formData.additionDate) errors.additionDate = 'Addition date is required.';
    if (!formData.putToUseDate) errors.putToUseDate = 'Put-to-use date is required.';
    const amount = parseFloat(formData.amount);
    if (!formData.amount || Number.isNaN(amount) || amount <= 0) errors.amount = 'Enter a positive amount.';
    if (formData.additionDate && formData.putToUseDate && formData.putToUseDate < formData.additionDate) {
      errors.putToUseDate = 'Put-to-use date cannot be before the addition date.';
    }
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    const resolved = resolveSelection(formData.classCode);
    const opt = context.classOptions.find(o => o.classCode === resolved.classCode);
    const saved: DraftAddition = {
      localId: entry?.localId ?? newLocalId(),
      scheduleId: opt?.scheduleId ?? entry?.scheduleId ?? '',
      scheduleClassId: opt ? opt.scheduleClassId : entry?.scheduleClassId ?? null,
      customClassLocalId: opt ? opt.customClassLocalId : entry?.customClassLocalId ?? null,
      classCode: resolved.classCode,
      subClassLocalId: resolved.subClassLocalId,
      ledgerName: formData.ledgerName.trim() || null,
      description: formData.description.trim(),
      additionDate: formData.additionDate,
      putToUseDate: formData.putToUseDate,
      amount,
      itBlockEnumId: formData.itBlockEnumId || null,
      itRateOverride: formData.itRateOverride !== '' ? parseFloat(formData.itRateOverride) || null : null,
      displayOrder: entry?.displayOrder ?? 0,
      // Recomputed authoritatively by recomputeDraft at tab level.
      daysUsed: null, isLt180Days: null,
      bookDepActual: null, bookDepRounded: null,
      itDepIndicativeActual: null, itDepIndicativeRounded: null,
    };
    onSave(saved);
    onClose();
  }, [formData, entry, context.classOptions, resolveSelection, onSave, onClose]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  if (!isOpen) return null;

  const fmt = (v: number) => roundTo(v, roundingPrecision)
    .toLocaleString('en-IN', { minimumFractionDigits: roundingPrecision, maximumFractionDigits: roundingPrecision });

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-md" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.add size={16} />
            <h2 className="modal-title" style={{ margin: 0 }}>
              {mode === 'create' ? 'Add Asset Addition' : 'Edit Asset Addition'}
            </h2>
          </div>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, overflow: 'auto' }}>
          <div className="form-group" data-field="description">
            <label className="form-label">Description <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${fieldErrors.description ? ' form-field-error' : ''}`}
              value={formData.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="e.g. Dell Latitude laptops x5"
              maxLength={300}
            />
            {fieldErrors.description && <div className="form-error-message">{fieldErrors.description}</div>}
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="classCode">
              <SyutSelect
                label={<>Asset Classification <span className="text-danger">*</span></>}
                value={formData.classCode}
                onChange={(v) => setField('classCode', (v as string) || '')}
                options={classOptions}
                isSearchable
                error={fieldErrors.classCode}
                placeholder="Select class…"
              />
            </div>
            <div className="form-group">
              <SyutSelect
                label={<>Ledger <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></>}
                value={formData.ledgerName}
                onChange={(v) => setField('ledgerName', (v as string) || '')}
                options={ledgerSelectOptions}
                isCreatable
                onCreateOption={(v) => setField('ledgerName', v)}
                formatCreateLabel={(v) => `Use "${v}"`}
                isSearchable
                isClearable
                placeholder="Pick a TB ledger or type…"
              />
            </div>
          </div>

          <div className="form-grid-3">
            <div className="form-group" data-field="additionDate">
              <label className="form-label">Addition Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${fieldErrors.additionDate ? ' form-field-error' : ''}`}
                value={formData.additionDate}
                onChange={(e) => setField('additionDate', e.target.value)}
              />
              {fieldErrors.additionDate && <div className="form-error-message">{fieldErrors.additionDate}</div>}
            </div>
            <div className="form-group" data-field="putToUseDate">
              <label className="form-label">Put-to-use Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${fieldErrors.putToUseDate ? ' form-field-error' : ''}`}
                value={formData.putToUseDate}
                onChange={(e) => setField('putToUseDate', e.target.value)}
              />
              {fieldErrors.putToUseDate && <div className="form-error-message">{fieldErrors.putToUseDate}</div>}
            </div>
            <div className="form-group" data-field="amount">
              <label className="form-label">Amount / Cost <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-input${fieldErrors.amount ? ' form-field-error' : ''}`}
                value={formData.amount}
                onChange={(e) => setField('amount', e.target.value)}
                min={0}
                step="0.01"
                style={{ textAlign: 'right' }}
              />
              {fieldErrors.amount && <div className="form-error-message">{fieldErrors.amount}</div>}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="itBlockEnumId">
              <SyutSelect
                label={
                  <>
                    Income Tax Block
                    {blockIsSuggested && (
                      <span style={{ marginLeft: 6, fontSize: '0.68rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                        suggested from class
                      </span>
                    )}
                  </>
                }
                value={formData.itBlockEnumId}
                onChange={(v) => setField('itBlockEnumId', (v as string) || '')}
                options={blockOptions}
                isSearchable
                placeholder="Select IT block…"
              />
            </div>
            <div className="form-group">
              <label className="form-label">IT Rate Override % <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="number"
                className="form-input"
                value={formData.itRateOverride}
                onChange={(e) => setField('itRateOverride', e.target.value)}
                min={0}
                max={100}
                step="0.01"
                placeholder={selectedBlock ? `block rate ${selectedBlock.rate}%` : ''}
                style={{ textAlign: 'right' }}
              />
            </div>
          </div>

          {/* Live computed preview */}
          {preview && (
            <div style={previewStripStyle}>
              <PreviewItem label="Days in use" value={String(preview.daysUsed)} />
              <PreviewItem
                label="IT treatment"
                value={preview.isLt180Days ? '< 180 days · half rate' : '≥ 180 days · full rate'}
              />
              <PreviewItem
                label={`Book dep. (${classConfig[formData.classCode]?.method_effective ?? 'WDV'})`}
                value={(classConfig[formData.classCode]?.book_rate ?? 0) > 0
                  ? fmt(preview.bookDep)
                  : 'set class rate first'}
              />
              <PreviewItem label="IT dep. (indicative)" value={fmt(preview.itDepIndicative)} />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>
            {mode === 'create' ? 'Add Addition' : 'Apply Changes'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes on this addition. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const PreviewItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)' }}>{label}</div>
    <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: 2, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

const previewStripStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
  gap: '0.75rem',
  padding: '0.65rem 0.85rem',
  marginTop: '0.25rem',
  borderRadius: 6,
  border: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};

export default FsaDepAdditionModal;
